export interface ScribeTranscriptState {
  committed: string;
  partial: string;
}

export interface ScribeEvent {
  message_type?: string;
  text?: string;
}

export function reduceScribeTranscript(
  state: ScribeTranscriptState,
  event: ScribeEvent
): ScribeTranscriptState {
  const text = typeof event.text === "string" ? event.text.trim() : "";
  if (event.message_type === "committed_transcript") {
    return {
      committed: [state.committed, text].filter(Boolean).join(" "),
      partial: ""
    };
  }
  if (event.message_type === "partial_transcript" || event.message_type === "final_transcript") {
    return { ...state, partial: text };
  }
  return state;
}

export function floatToPcm16(
  input: Float32Array,
  inputRate: number,
  outputRate = 16_000
): Uint8Array {
  const ratio = Math.max(1, inputRate / outputRate);
  const outputLength = Math.max(1, Math.floor(input.length / ratio));
  const bytes = new Uint8Array(outputLength * 2);
  const view = new DataView(bytes.buffer);
  for (let outputIndex = 0; outputIndex < outputLength; outputIndex += 1) {
    const start = Math.floor(outputIndex * ratio);
    const end = Math.min(input.length, Math.max(start + 1, Math.floor((outputIndex + 1) * ratio)));
    let sum = 0;
    for (let inputIndex = start; inputIndex < end; inputIndex += 1) sum += input[inputIndex];
    const sample = Math.max(-1, Math.min(1, sum / Math.max(1, end - start)));
    view.setInt16(outputIndex * 2, sample < 0 ? sample * 0x8000 : sample * 0x7fff, true);
  }
  return bytes;
}

export interface ScribeRealtimeCallbacks {
  onTranscript(text: string): void;
  onFailure(): void;
}

export class ScribeRealtimeSession {
  private socket: WebSocket | null = null;
  private context: AudioContext | null = null;
  private source: MediaStreamAudioSourceNode | null = null;
  private processor: ScriptProcessorNode | null = null;
  private state: ScribeTranscriptState = { committed: "", partial: "" };
  private stopping = false;
  private commitReceived: (() => void) | null = null;

  constructor(private readonly callbacks: ScribeRealtimeCallbacks) {}

  async start(stream: MediaStream, csrfToken: string): Promise<void> {
    const response = await fetch("/api/transcribe-realtime-token", {
      method: "POST",
      credentials: "include",
      headers: { "X-Launchpad-CSRF": csrfToken }
    });
    const body = await response.json().catch(() => null) as {
      token?: string;
      websocketUrl?: string;
    } | null;
    if (!response.ok || !body?.token || !body.websocketUrl) throw new Error("scribe_realtime_unavailable");
    const url = new URL(body.websocketUrl);
    url.searchParams.set("token", body.token);
    url.searchParams.set("model_id", "scribe_v2_realtime");
    url.searchParams.set("audio_format", "pcm_16000");
    const socket = new WebSocket(url);
    this.socket = socket;
    await new Promise<void>((resolve, reject) => {
      const timeout = window.setTimeout(() => reject(new Error("scribe_realtime_timeout")), 5_000);
      socket.addEventListener("open", () => {
        window.clearTimeout(timeout);
        resolve();
      }, { once: true });
      socket.addEventListener("error", () => {
        window.clearTimeout(timeout);
        reject(new Error("scribe_realtime_socket"));
      }, { once: true });
    });
    socket.addEventListener("message", (event) => this.receive(event.data));
    socket.addEventListener("close", () => {
      if (!this.stopping) this.callbacks.onFailure();
    });
    socket.addEventListener("error", () => {
      if (!this.stopping) this.callbacks.onFailure();
    });
    const context = new AudioContext();
    await context.resume();
    const source = context.createMediaStreamSource(stream);
    const processor = context.createScriptProcessor(4096, 1, 1);
    processor.onaudioprocess = (event) => {
      if (socket.readyState !== WebSocket.OPEN || this.stopping) return;
      const pcm = floatToPcm16(event.inputBuffer.getChannelData(0), context.sampleRate);
      socket.send(JSON.stringify({
        message_type: "input_audio_chunk",
        audio_base_64: bytesToBase64(pcm)
      }));
      event.outputBuffer.getChannelData(0).fill(0);
    };
    source.connect(processor);
    processor.connect(context.destination);
    this.context = context;
    this.source = source;
    this.processor = processor;
  }

  async stop(): Promise<string> {
    this.stopping = true;
    const socket = this.socket;
    if (socket?.readyState === WebSocket.OPEN) {
      const committed = new Promise<void>((resolve) => {
        this.commitReceived = resolve;
      });
      socket.send(JSON.stringify({
        message_type: "input_audio_chunk",
        audio_base_64: "",
        commit: true
      }));
      await Promise.race([
        committed,
        new Promise<void>((resolve) => window.setTimeout(resolve, 1_200))
      ]);
    }
    const text = [this.state.committed, this.state.partial].filter(Boolean).join(" ").trim();
    await this.close();
    return text;
  }

  async close(): Promise<void> {
    this.stopping = true;
    this.processor?.disconnect();
    this.source?.disconnect();
    this.processor = null;
    this.source = null;
    if (this.context) await this.context.close().catch(() => undefined);
    this.context = null;
    this.socket?.close();
    this.socket = null;
  }

  private receive(raw: unknown): void {
    if (typeof raw !== "string") return;
    let event: ScribeEvent;
    try {
      event = JSON.parse(raw) as ScribeEvent;
    } catch {
      return;
    }
    this.state = reduceScribeTranscript(this.state, event);
    if (event.message_type === "committed_transcript") this.commitReceived?.();
    if (event.message_type === "partial_transcript" ||
        event.message_type === "final_transcript" ||
        event.message_type === "committed_transcript") {
      this.callbacks.onTranscript([this.state.committed, this.state.partial].filter(Boolean).join(" ").trim());
      return;
    }
    if (event.message_type && [
      "auth_error",
      "quota_exceeded",
      "transcriber_error",
      "input_error",
      "error",
      "rate_limited",
      "queue_overflow",
      "resource_exhausted",
      "session_time_limit_exceeded",
      "chunk_size_exceeded"
    ].includes(event.message_type)) {
      this.callbacks.onFailure();
    }
  }
}

function bytesToBase64(bytes: Uint8Array): string {
  let binary = "";
  for (let index = 0; index < bytes.length; index += 1) binary += String.fromCharCode(bytes[index]);
  return btoa(binary);
}
